import { useQuery } from '@apollo/client/react';
import i18n from 'i18next';
import React from 'react';
import Loading from '@components/core/loading';
import { AuditServiceType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import {
    getFieldChangeItemsAuditRecord,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import { useLocalState } from '@hooks/index';
import Constants, { GeneratedReportStatusNames } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { getPrefixByService } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { GET_DATASETS } from '@queries/central-monitoring';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';
import { dataSets, dataSetsVariables } from '@queries/generated/central-monitoring.generated';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { useGetGeneratedReportModuleType } from '@apis/bff-api/report-templates';

interface Dataset {
    id: string;
    text: string | null;
}

const findDatasetNameById = (datasets: Dataset[], datasetId?: string) => {
    if (!datasetId) return null;
    return datasets?.find((d) => d?.id === datasetId)?.text ?? null;
};

const joinSignatoriesPropsToString = (recordData: Record<string, any> | null, recordIdPrefix?: string) => {
    return ['DisplayOrder', 'FirstName', 'LastName', 'JobTitle', 'RecordId']
        .map((prop) => {
            const propName = i18n.t(`auditLog:${prop}`);
            const recordValue = !!recordData && prop in recordData ? recordData?.[prop] : '--';
            const propValue = prop === 'RecordId' ? `${recordIdPrefix}${recordValue}` : recordValue;
            return `${propName}: ${propValue}`;
        })
        .join('; ');
};

const propArray = ['BlobPath', 'DatasetId', 'ComparisonDatasetId'];

const GeneratedReportRecordChangesGridPBI = ({ oldRecord, newRecord, oldItem, newItem }: any) => {
    const localState = useLocalState();
    const studyId = newRecord?.StudyId || oldRecord?.StudyId || localState.state?.selectedStudy?.id;

    // Load the module type for the associated template so we know where to load datasets from
    const { isLoading, data: moduleType } = useGetGeneratedReportModuleType(newRecord?._id);

    const { data: dataSetsData, loading: isLoadingDataSets } = useQuery<dataSets, dataSetsVariables>(GET_DATASETS, {
        variables: { studyId },
        skip:
            !studyId ||
            (!oldRecord?.DatasetId && !newRecord?.DatasetId) ||
            (moduleType !== null && moduleType === Constants.Modules.CM2),
    });
    const { data: prefixesData, loading: isLoadingPrefixes } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );
    const { data: cm2DataSetsData, isLoading: loadingCM2Data } = DatasetApi.useGetAll(
        studyId,
        moduleType !== null && moduleType !== Constants.Modules.CM2,
    );

    if (isLoadingDataSets || isLoadingPrefixes || isLoading || loadingCM2Data) return <Loading float />;

    let allDataSets: Dataset[];
    if (moduleType === Constants.Modules.CM2) {
        allDataSets = (cm2DataSetsData ?? []).map((dataSet) => ({
            id: dataSet?.id!.toString(),
            text: stringDateWithoutTimeFromUtc(dataSet?.dataCutDate, 'DD-MMM-YYYY'),
        }));
    } else {
        allDataSets = (dataSetsData?.dataSets ?? []).map((dataSet) => ({
            id: dataSet?.id,
            text: stringDateWithoutTimeFromUtc(dataSet?.dataCutDate, 'DD-MMM-YYYY'),
        }));
    }

    const oldRecordDatasetName = findDatasetNameById(allDataSets, oldRecord?.DatasetId);
    const oldRecordComparisonDatasetName = findDatasetNameById(allDataSets, oldRecord?.ComparisonDatasetId);
    const newRecordDatasetName = findDatasetNameById(allDataSets, newRecord?.DatasetId);
    const newRecordComparisonDatasetName = findDatasetNameById(allDataSets, newRecord?.ComparisonDatasetId);

    const refactoredOldRecord = {
        ...oldRecord,
        ...(oldRecordDatasetName ? { Dataset: oldRecordDatasetName } : {}),
        ...(oldRecordComparisonDatasetName ? { ComparisonDataset: oldRecordComparisonDatasetName } : {}),
        ...removePropertiesFromObject(oldRecord, propArray),
    };
    const refactoredNewRecord = {
        ...newRecord,
        ...(newRecordDatasetName ? { Dataset: newRecordDatasetName } : {}),
        ...(newRecordComparisonDatasetName ? { ComparisonDataset: newRecordComparisonDatasetName } : {}),
        ...removePropertiesFromObject(newRecord, propArray),
    };

    const arrayOfObjects = {
        Signatories: {
            valueResolver: (recordData: any, item: any) =>
                joinSignatoriesPropsToString(
                    recordData,
                    getPrefixByService(prefixesData?.auditRecordIdPrefixes || [], AuditServiceType.REPORTING),
                ),
        },
    };

    const enums: Record<string, any[]> = {
        Status: ['', ...Object.values(GeneratedReportStatusNames)],
    };

    const items = getFieldChangeItemsAuditRecord(refactoredOldRecord, refactoredNewRecord, null, (field: string) => {
        return getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            arrayOfObjects,
        });
    });

    if (isLoadingPrefixes) return <Loading float />;

    return <FieldChangeGrid items={items} />;
};

export { GeneratedReportRecordChangesGridPBI };
